import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, where, updateDoc, doc, getDocs } from 'firebase/firestore';
import { Html5Qrcode } from 'html5-qrcode';
import { auth, db } from '../config/firebase';
import './CheckInPage.css';

// Icon Components
const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DashboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 1C0 0.45 0.45 0 1 0H6C6.55 0 7 0.45 7 1V6C7 6.55 6.55 7 6 7H1C0.45 7 0 6.55 0 6V1ZM9 1C9 0.45 9.45 0 10 0H15C15.55 0 16 0.45 16 1V6C16 6.55 15.55 7 15 7H10C9.45 7 9 6.55 9 6V1ZM0 10C0 9.45 0.45 9 1 9H6C6.55 9 7 9.45 7 10V15C7 15.55 6.55 16 6 16H1C0.45 16 0 15.55 0 15V10ZM9 10C9 9.45 9.45 9 10 9H15C15.55 9 16 9.45 16 10V15C16 15.55 15.55 16 15 16H10C9.45 16 9 15.55 9 15V10Z" fill="currentColor" />
  </svg>
);

const RegistrationsIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
    <path d="M0 0V16H20V0H0ZM2 2H18V14H2V2ZM4 4H16V6H4V4ZM4 8H16V10H4V8ZM4 12H12V14H4V12Z" fill="currentColor" />
  </svg>
);

const TicketIcon = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
    <path d="M0 2C0 1.45 0.45 1 1 1H17C17.55 1 18 1.45 18 2V4C17.45 4 17 4.45 17 5C17 5.55 17.45 6 18 6V8C18 8.55 17.55 9 17 9H1C0.45 9 0 8.55 0 8V6C0.55 6 1 5.55 1 5C1 4.45 0.55 4 0 4V2Z" fill="currentColor" />
  </svg>
);

const CheckInIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path d="M0 1V15H14V1H0ZM2 3H12V13H2V3ZM6 5H8V7H6V5ZM6 9H8V11H6V9Z" fill="currentColor" />
  </svg>
);

const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 0V16H16V0H0ZM2 2H14V14H2V2ZM4 4H12V6H4V4ZM4 8H12V10H4V8ZM4 12H8V14H4V12Z" fill="currentColor" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 14H2C1.45 14 1 13.55 1 13V3C1 2.45 1.45 2 2 2H6V0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H6V14ZM11.59 10.59L9.59 12.59L11.18 14.18L15.36 10L11.18 5.82L9.59 7.41L11.59 9.41H5V11.41H11.59Z" fill="currentColor" />
  </svg>
);

const QRIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M0 0H8V8H0V0ZM2 2V6H6V2H2ZM12 0H20V8H12V0ZM14 2V6H18V2H14ZM0 12H8V20H0V12ZM2 14V18H6V14H2ZM10 12H12V14H10V12ZM14 12H16V14H14V12ZM18 12H20V14H18V12ZM10 16H12V18H10V16ZM14 16H20V20H14V16ZM16 18V20H18V18H16Z" fill="currentColor" />
  </svg>
);

const CameraIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
    <path d="M18 2H14.83L13.41 0.59C13.05 0.23 12.55 0 12 0H8C7.45 0 6.95 0.23 6.59 0.59L5.17 2H2C0.9 2 0 2.9 0 4V14C0 15.1 0.9 16 2 16H18C19.1 16 20 15.1 20 14V4C20 2.9 19.1 2 18 2ZM18 14H2V4H5.17L6.59 5.41C6.95 5.77 7.45 6 8 6H12C12.55 6 13.05 5.77 13.41 5.41L14.83 4H18V14ZM10 5C7.24 5 5 7.24 5 10C5 12.76 7.24 15 10 15C12.76 15 15 12.76 15 10C15 7.24 12.76 5 10 5ZM10 13C8.35 13 7 11.65 7 10C7 8.35 8.35 7 10 7C11.65 7 13 8.35 13 10C13 11.65 11.65 13 10 13Z" fill="currentColor" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M9 0C4.03 0 0 4.03 0 9C0 13.97 4.03 18 9 18C13.97 18 18 13.97 18 9C18 4.03 13.97 0 9 0ZM7 13.5L2.5 9L3.91 7.59L7 10.67L14.09 3.58L15.5 5L7 13.5Z" fill="currentColor" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M12.5 11H11.71L11.43 10.73C12.41 9.59 13 8.11 13 6.5C13 3.46 10.54 1 7.5 1C4.46 1 2 3.46 2 6.5C2 9.54 4.46 12 7.5 12C9.11 12 10.59 11.41 11.73 10.43L12 10.71V11.5L16 15.49L15.49 16L11.5 12ZM7.5 12C4.96 12 3 10.04 3 7.5C3 4.96 4.96 3 7.5 3C10.04 3 12 4.96 12 7.5C12 10.04 10.04 12 7.5 12Z" fill="currentColor" />
  </svg>
);


const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 12.5C11.38 12.5 12.5 11.38 12.5 10C12.5 8.62 11.38 7.5 10 7.5C8.62 7.5 7.5 8.62 7.5 10C7.5 11.38 8.62 12.5 10 12.5ZM15.92 6.42L14.5 5C14.5 3.62 13.38 2.5 12 2.5L10.58 1.08C9.2 1.08 8.08 2.2 8.08 3.58L6.66 5C5.28 5 4.16 6.12 4.16 7.5L2.74 8.92C2.74 10.3 3.86 11.42 5.24 11.42L6.66 12.84C6.66 14.22 7.78 15.34 9.16 15.34L10.58 16.76C11.96 16.76 13.08 15.64 13.08 14.26L14.5 12.84C15.88 12.84 17 11.72 17 10.34L18.42 8.92C18.42 7.54 17.3 6.42 15.92 6.42Z" fill="currentColor" />
  </svg>
);

const BroadcastIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2H18C19.1 2 20 2.9 20 4V12C20 13.1 19.1 14 18 14H2C0.9 14 0 13.1 0 12V4C0 2.9 0.9 2 2 2ZM18 4L10 8.5L2 4V4L10 0.5L18 4ZM2 6V12H18V6L10 10.5L2 6Z" fill="currentColor" />
  </svg>
);

function CheckInPage() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [checkedInTicket, setCheckedInTicket] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified
        });
        fetchRegistrations();
      } else {
        navigate('/admin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    // Cleanup scanner on unmount
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => { });
      }
    };
  }, []);

  const fetchRegistrations = () => {
    try {
      const registrationsRef = collection(db, 'registrations');
      const q = query(registrationsRef, orderBy('registeredAt', 'desc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const registrationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRegistrations(registrationsData);
      }, (error) => {
        console.error('Error fetching registrations:', error);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up registrations listener:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('adminUser');
      sessionStorage.removeItem('adminUser');
      navigate('/admin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCheckIn = async (scannedTicketIdOrEvent = null) => {
    let scannedTicketId = null;

    // If called from a button onClick, the first argument is a React event.
    if (
      scannedTicketIdOrEvent &&
      typeof scannedTicketIdOrEvent === 'object' &&
      'preventDefault' in scannedTicketIdOrEvent
    ) {
      scannedTicketIdOrEvent.preventDefault();
    } else {
      scannedTicketId = scannedTicketIdOrEvent;
    }

    const rawId = scannedTicketId !== null ? scannedTicketId : ticketId;
    let idToCheck = (rawId ?? '').toString().trim().toUpperCase();

    if (!idToCheck) {
      setMessage({ type: 'error', text: 'Please enter or scan a ticket ID' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    // Normalize the ticket ID: if it doesn't start with "MTU-OSH", prepend it
    // If it's just 7 digits, prepend "MTU-OSH"
    if (/^\d{7}$/.test(idToCheck)) {
      // Just 7 digits, prepend prefix
      idToCheck = `MTU-OSH${idToCheck}`;
    } else if (!idToCheck.startsWith('MTU-OSH') || !/^MTU-OSH\d{7}$/.test(idToCheck)) {
      // Invalid format
      setMessage({ type: 'error', text: 'Invalid ticket ID format. Please enter 7 digits.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    try {
      // Find ticket by ticket ID
      const ticketsRef = collection(db, 'tickets');
      const q = query(ticketsRef, where('ticketId', '==', idToCheck));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setMessage({ type: 'error', text: `Ticket ${idToCheck} not found` });
        setCheckedInTicket(null);
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        return;
      }

      const ticketDoc = querySnapshot.docs[0];
      const ticket = { id: ticketDoc.id, ...ticketDoc.data() };

      if (ticket.status === 'disabled') {
        setMessage({ type: 'error', text: 'Ticket disabled. Please contact admin.' });
        setCheckedInTicket(null);
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        return;
      }

      // Check if ticket is already used
      if (ticket.used === true) {
        setMessage({ type: 'error', text: 'Ticket already checked in' });
        setCheckedInTicket(null);
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        return;
      }

      // Mark ticket as used
      const ticketRef = doc(db, 'tickets', ticket.id);
      await updateDoc(ticketRef, {
        used: true,
        checkedInAt: new Date(),
        checkedInBy: user?.email || 'admin'
      });

      // Update registration status to checked-in if it exists
      if (ticket.registrationId) {
        try {
          const registrationRef = doc(db, 'registrations', ticket.registrationId);
          await updateDoc(registrationRef, {
            status: 'checked-in',
            checkedInAt: new Date()
          });
        } catch (regError) {
          console.error('Error updating registration:', regError);
        }
      }

      setCheckedInTicket(ticket);
      setMessage({
        type: 'success',
        text: `Successfully checked in! ${ticket.userName || ticket.userEmail}`
      });
      setTicketId('');
      setTimeout(() => {
        setMessage({ type: '', text: '' });
        setCheckedInTicket(null);
      }, 5000);
    } catch (error) {
      console.error('Error checking in:', error);
      setMessage({ type: 'error', text: 'Error processing check-in' });
      setCheckedInTicket(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleStartScanning = async () => {
    if (scanning) {
      // Stop scanning
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
          html5QrCodeRef.current.clear();
        } catch (error) {
          console.error('Error stopping scanner:', error);
        }
      }
      setScanning(false);
      setMessage({ type: '', text: '' });
      return;
    }

    // Start scanning
    try {
      const html5QrCode = new Html5Qrcode(scannerRef.current.id);
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          // QR code scanned successfully
          // Normalize the scanned text - it might be just the 7 digits or the full ID
          let normalizedId = decodedText.trim().toUpperCase();

          // If it's just 7 digits, prepend "MTU-OSH"
          if (/^\d{7}$/.test(normalizedId)) {
            normalizedId = `MTU-OSH${normalizedId}`;
          }
          // If it already has the prefix, use as is
          else if (!normalizedId.startsWith('MTU-OSH')) {
            // Try to extract 7 digits from the end if it's in a different format
            const digitsMatch = normalizedId.match(/\d{7}$/);
            if (digitsMatch) {
              normalizedId = `MTU-OSH${digitsMatch[0]}`;
            }
          }

          handleCheckIn(normalizedId);
          html5QrCode.stop();
          setScanning(false);
        },
        (errorMessage) => {
          // Ignore scanning errors (they're frequent during scanning)
        }
      );

      setScanning(true);
      setMessage({ type: 'info', text: 'Position QR code within the frame' });
    } catch (error) {
      console.error('Error starting scanner:', error);
      setMessage({ type: 'error', text: 'Failed to start camera. Please check permissions.' });
      setScanning(false);
    }
  };

  const handleSwitchCamera = () => {
    // Camera switching logic would go here
    setMessage({ type: 'info', text: 'Camera switched' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Calculate statistics
  const totalCheckIns = registrations.filter(r => r.status === 'checked-in').length;
  const todayCheckIns = registrations.filter(r => {
    if (r.status !== 'checked-in' || !r.checkedInAt) return false;
    const checkInDate = r.checkedInAt.toDate ? r.checkedInAt.toDate() : new Date(r.checkedInAt);
    const today = new Date();
    return checkInDate.toDateString() === today.toDateString();
  }).length;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavClick = (path) => {
    closeSidebar();
    navigate(path);
  };

  return (
    <div className="checkin-page-container">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Close Button for Mobile */}
        <button className="sidebar-close-button" onClick={closeSidebar} aria-label="Close menu">
          <CloseIcon />
        </button>

        {/* Logo Section */}
        <div className="sidebar-logo">
          <div className="logo-icon">
            <img
              src={process.env.PUBLIC_URL + '/logo.jpg'}
              alt="Mountain Top University"
              className="admin-dashboard-logo-image"
            />
          </div>
          <div className="logo-text">
            <div className="logo-title">MTU Alumni</div>
            <div className="logo-subtitle">Event Management</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => handleNavClick('/admin/dashboard')}>
            <DashboardIcon />
            <span>Dashboard</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/registrations')}>
            <RegistrationsIcon />
            <span>Registrations</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/generate-ticket')}>
            <TicketIcon />
            <span>Generate Ticket</span>
          </div>
          <div className="nav-item active" onClick={closeSidebar}>
            <CheckInIcon />
            <span>Check-in</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/broadcasts')}>
            <BroadcastIcon />
            <span>Email Broadcast</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/dashboard?export=1')}>
            <ExportIcon />
            <span>Export Data</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/settings')}>
            <SettingsIcon />
            <span>Settings</span>
          </div>
        </nav>

        {/* User Profile Section */}
        <div className="sidebar-user">
          <div className="user-info">
            <div className="user-avatar">
              {getUserInitials(user?.email || 'Admin User')}
            </div>
            <div className="user-details">
              <div className="user-name">Admin User</div>
              <div className="user-email">{user?.email || 'admin@mtu.edu'}</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <LogoutIcon />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="checkin-main">
        {/* Header */}
        <header className="checkin-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="header-title">Event Check-in</h1>
              <p className="header-subtitle">Scan or enter ticket information</p>
            </div>
            <div className="header-right">
              <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle menu">
                <HamburgerIcon />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="checkin-content">
          {/* Statistics Section - moved to top */}
          <div className="stats-section">
            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-info">
                  <div className="stat-label">Total Check-ins</div>
                  <div className="stat-value">{totalCheckIns}</div>
                </div>
                <div className="stat-icon blue">
                  <CheckInIcon />
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-info">
                  <div className="stat-label">Today's Check-ins</div>
                  <div className="stat-value">{todayCheckIns}</div>
                </div>
                <div className="stat-icon green">
                  <CheckInIcon />
                </div>
              </div>
            </div>
          </div>

          <div className="checkin-sections">
            {/* QR Code Scanner Section */}
            <div className="scanner-section">
              <div className="section-header">
                <h2 className="section-title">QR Code Scanner</h2>
                <p className="section-subtitle">Position the QR code within the scanning area</p>
              </div>

              <div className="scanner-area">
                <div id="qr-reader" ref={scannerRef} style={{ width: '100%', minHeight: '320px' }}></div>
                {!scanning && (
                  <div className="scanner-placeholder">
                    <QRIcon />
                    <p>Click "Start Scanning" to begin</p>
                  </div>
                )}
              </div>

              <div className="scanner-actions">
                <button
                  className={`action-button primary ${scanning ? 'active' : ''}`}
                  onClick={handleStartScanning}
                >
                  <QRIcon />
                  <span>{scanning ? 'Stop Scanning' : 'Start Scanning'}</span>
                </button>
                <button
                  className="action-button secondary"
                  onClick={handleSwitchCamera}
                >
                  <CameraIcon />
                  <span>Switch Camera</span>
                </button>
              </div>
            </div>

            {/* Manual Entry Section */}
            <div className="manual-entry-section">
              <div className="section-header">
                <h2 className="section-title">Manual Entry</h2>
                <p className="section-subtitle">Enter ticket ID manually if QR code is not available</p>
              </div>

              <div className="entry-form">
                <label className="form-label">Ticket ID</label>
                <div className="input-wrapper">
                  <span className="ticket-prefix">MTU-OSH</span>
                  <input
                    type="text"
                    className="ticket-input"
                    placeholder="0000000"
                    value={ticketId}
                    onChange={(e) => {
                      // Only allow digits, max 7 characters
                      const value = e.target.value.replace(/\D/g, '').slice(0, 7);
                      setTicketId(value);
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleCheckIn()}
                    maxLength={7}
                  />
                  <SearchIcon />
                </div>
                <button
                  className="checkin-button"
                  onClick={handleCheckIn}
                >
                  <CheckIcon />
                  <span>Check In</span>
                </button>
              </div>

              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}

              {checkedInTicket && (
                <div className="checked-in-info">
                  <div className="checked-in-header">
                    <CheckIcon />
                    <span>Checked In Successfully</span>
                  </div>
                  <div className="checked-in-details">
                    <p><strong>Name:</strong> {checkedInTicket.userName || checkedInTicket.userEmail}</p>
                    <p><strong>Ticket ID:</strong> {checkedInTicket.ticketId}</p>
                    <p><strong>Email:</strong> {checkedInTicket.userEmail}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Statistics Section */}
          {/* Stats section removed from bottom; now shown above */}
        </div>
      </main>
    </div>
  );
}

export default CheckInPage;

